import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, TextFieldProps, CircularProgress } from '@mui/material';

export interface MasterOption {
  value: string | number;
  label: string;
  stateName?: string;
  stateId?: number;
}

interface MasterDropdownProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'select'> {
  options: MasterOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number | null) => void;
  loading?: boolean;
  placeholder?: string;
  getOptionLabel?: (option: MasterOption) => string;
  isOptionEqualToValue?: (option: MasterOption, value: MasterOption | null) => boolean;
  filterOptions?: (options: MasterOption[], state: { inputValue: string }) => MasterOption[];
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  disableClearable?: boolean;
}

const MasterDropdown: React.FC<MasterDropdownProps> = ({
  options = [],
  value,
  onChange,
  loading = false,
  label,
  placeholder,
  required = false,
  disabled = false,
  error = false,
  disableClearable,
  helperText,
  getOptionLabel = (option) => option.label || String(option.value),
  isOptionEqualToValue = (option, val) => option.value === val?.value,
  filterOptions,
  fullWidth = true,
  size,
  sx,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    console.log('disableClearable:', disableClearable)
  },[])

  // Find the selected option based on value
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return options.find((opt) => opt.value === value) || null;
  }, [value, options]);

  // Update input value when selected option changes
  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else {
      setInputValue('');
    }
  }, [selectedOption]);

  // Default filter function for searchable dropdown
  const defaultFilterOptions = (options: MasterOption[], state: { inputValue: string }) => {
    if (!state.inputValue) {
      return options;
    }
    const searchTerm = state.inputValue.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm)
    );
  };

  const handleChange = (event: any, newValue: MasterOption | null) => {
    onChange(newValue ? newValue.value : null);
    setOpen(false); // Close dropdown after selection
  };

  // Cleanup: Close dropdown when component unmounts
  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, []);

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={filterOptions || defaultFilterOptions}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      disablePortal={false}
      disableClearable={disableClearable ?? true}
      
      componentsProps={{
        popper: {
          style: {
            zIndex: 1400, // Higher than side panel z-index (1300+)
          },
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ],
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          // autoComplete="off"
          autoComplete="new-password"
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiOutlinedInput-input": {
              display: "flex",
              alignItems: "center",
              height: "100%",
            },
            "& .MuiInputBase-input": {
              display: "flex",
              alignItems: "center",
              "&::placeholder": {
                opacity: 1,
                display: "flex",
                alignItems: "center",
              },
            },
            "& input": {
              display: "flex",
              alignItems: "center",
              "&::placeholder": {
                opacity: 1,
              },
            },
            ...sx,
          }}
          {...rest}
        />
      )}
    />
  );
};

export default MasterDropdown;

